import { useEffect, useState } from 'react';
import {
  INITIAL_AUTH_STATE,
  completeRedirectSignIn,
  signInWithGoogle,
  signOut,
  watchAuth,
  type AuthState,
} from '../data/auth';
import { AppShell } from './shell/AppShell';
import './styles.css';

export function App() {
  const [state, setState] = useState<AuthState>(INITIAL_AUTH_STATE);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchAuth(setState), []);

  // Surface a failed redirect sign-in. Without this the user lands back on the sign-in
  // screen with no explanation of why nothing happened.
  useEffect(() => {
    void completeRedirectSignIn().then((redirectError) => {
      if (redirectError) setError(redirectError.message);
    });
  }, []);

  async function handleSignIn() {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (cause) {
      const code = (cause as { code?: string }).code;
      // The user closing the popup is not an error worth shouting about.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
      setError(cause instanceof Error ? cause.message : 'Sign-in failed');
    }
  }

  if (state.status === 'signed-in' && state.record) {
    return <AppShell record={state.record} preferences={state.record.preferences} />;
  }

  return (
    <main className="centered">
      <h1>Expenses</h1>

      {state.status === 'loading' && <p className="muted">Checking sign-in…</p>}

      {state.status === 'signed-out' && (
        <>
          <p className="muted">Sign in to continue.</p>
          <button type="button" className="btn btn-primary" onClick={handleSignIn}>
            Sign in with Google
          </button>
        </>
      )}

      {/* A valid Google session with no capability record — where anyone who is not the
          owner lands. Say so plainly rather than showing an empty app. */}
      {state.status === 'no-record' && (
        <>
          <p className="muted">
            Signed in as <strong>{state.user?.email}</strong>, but this account has no access
            to this book.
          </p>
          <button type="button" className="btn btn-quiet" onClick={signOut}>
            Sign out
          </button>
        </>
      )}

      {error && <p className="error">{error}</p>}
    </main>
  );
}
